import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useEffect, useState } from 'react';
import { _single } from '../../../core/services/dataBean/EntityCatalog';
import { Alert } from './Alert';
import useModal, { MODAL_SM } from './hooks/useModal';
import { Modal } from './Modal';

type DataType = 'string' | 'number' | 'date';

interface ColumnDefinition {
  header: string;
  key: string;
  type: DataType;
}

interface ListItem {
  codigo: string;
  valor: string;
}

interface ListProps {
  sheetName: string;
  data: ListItem[];
}

interface ExportExcelProps {
  lists: ListProps[];
  dataColumns: ColumnDefinition[];
  fileName?: string;
  download: boolean;
}
// Ejemplo
// const descargarPlantilla = ()=>{
//     const lists = [
//         {
//           sheetName: 'Conceptos',
//           data: [
//             { codigo: '001', valor: 'Manzana' },
//             { codigo: '002', valor: 'Banana' }
//           ]
//         },
//         {
//           sheetName: 'Naturaleza',
//           data: [
//             { codigo: 'A', valor: 'Activo' },
//             { codigo: 'P', valor: 'Pasivo' }
//           ]
//         },
//         {
//           sheetName: 'Genero',
//           data: [
//             { codigo: 'H', valor: 'Hombre' },
//             { codigo: 'M', valor: 'Mujer' }
//           ]
//         }
//       ];

//       const dataColumns:any = [
//         { header: 'Nombre', key: 'nombre', type: 'string' },
//         { header: 'Edad', key: 'edad', type: 'number' },
//         { header: 'Fecha de Nacimiento', key: 'fechaNacimiento', type: 'date' }
//       ];
//       return (
//         <div>              
//           <ExportExcelComponent lists={lists} dataColumns={dataColumns} />
//         </div>
//       );
// }

export const ExportExcelComponent = ({ lists, dataColumns, fileName = 'listas.xlsx', download }: ExportExcelProps) => {

  const [view, setView] = useState<boolean>(false);
  const modalFormat = useModal("Formato", MODAL_SM);
  const s2ab = (s: string) => {
    const buf = new ArrayBuffer(s.length);
    const view = new Uint8Array(buf);
    for (let i = 0; i !== s.length; ++i) {
      view[i] = s.charCodeAt(i) & 0xFF;
    }
    return buf;
  };

  useEffect(() => {
    if (download === true) {
      exportToExcel();
      modalFormat.open();
    }
  }, [download])

  const buildFileName = (baseName: string) => {
    const now = new Date();
    const yy = now.getFullYear().toString().slice(-2);
    const MM = (now.getMonth() + 1).toString().padStart(2, '0');
    const dd = now.getDate().toString().padStart(2, '0');
    const hh = now.getHours().toString().padStart(2, '0');
    const mm = now.getMinutes().toString().padStart(2, '0');

    return `${baseName}_${yy}${MM}${dd}_${hh}${mm}.xlsx`;
  };


  const exportToExcel = () => {
    const workbook = XLSX.utils.book_new();

    if (!lists || !dataColumns) {
      return;
    }

    // Crear hojas de listas
    lists.forEach(({ sheetName, data }) => {
      const worksheetData = data.map(({ codigo, valor }) => ({
        Código: codigo,
        Valor: valor
      }));

      const worksheet = XLSX.utils.json_to_sheet(worksheetData, { header: ['Código', 'Valor'] });
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });

    // Crear HojaDatos
    const headers = [
      ...dataColumns.map(c => c.header),
      ...lists.map(l => l.sheetName)
    ];

    const emptyRow = headers.reduce((acc, header) => {
      acc[header] = '';
      return acc;
    }, {} as Record<string, any>);

    const hojaDatos = XLSX.utils.json_to_sheet([emptyRow], { header: headers });

    // Configurar estilos básicos: negrita y fondo para la fila de encabezados
    const ref = hojaDatos['!ref'];
    if (ref) {
      const range = XLSX.utils.decode_range(ref);

      for (let C = range.s.c; C <= range.e.c; ++C) {
        const address = XLSX.utils.encode_cell({ r: 0, c: C });

        if (!hojaDatos[address]) continue;

        // Aplicar formato
        hojaDatos[address].s = {
          font: { bold: true },
          fill: {
            patternType: "solid",
            fgColor: { rgb: "D9E1F2" } // Azul clarito
          }
        };
      }

      // Ajuste automático de anchos
      const colWidths = headers.map((header) => ({ wch: Math.max(12, header.length + 5) }));
      hojaDatos['!cols'] = colWidths;
    }

    // Crear validaciones de lista
    if (!hojaDatos['!dataValidation']) hojaDatos['!dataValidation'] = [];

    if (ref) {
      const range = XLSX.utils.decode_range(ref);

      for (let C = 0; C <= range.e.c; ++C) {
        const address = XLSX.utils.encode_cell({ r: 0, c: C });
        const headerName = hojaDatos[address]?.v;

        const listInfo = lists.find(l => l.sheetName === headerName);
        if (listInfo) {
          const colLetter = XLSX.utils.encode_col(C);
          const formula = `'${listInfo.sheetName}'!$B$2:$B$${listInfo.data.length + 1}`;

          hojaDatos['!dataValidation'].push({
            sqref: `${colLetter}2:${colLetter}100`,
            type: 'list',
            allowBlank: true,
            showInputMessage: true,
            formula1: formula,
          });
        }
      }
    }

    // Agregar hoja de datos
    XLSX.utils.book_append_sheet(workbook, hojaDatos, 'HojaDatos');

    // Guardar archivo
    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'binary', cellStyles: true });
    const blob = new Blob([s2ab(wbout)], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, buildFileName(fileName));
  };

  return (

    <Modal modal={modalFormat.modal} updateModal={modalFormat.setModal}>
      <Alert clase='primary'>
        Descargando Formato.
      </Alert>
    </Modal>

  );
};
